import type { CartItemPrices } from '@cobrastyle/shared-types';

/**
 * Cart line prices, including VAT.
 *
 * The store displays prices including tax — `tax/display/type`, `tax/cart_display/price` and
 * `tax/cart_display/subtotal` are all set to 2, and catalogue prices are entered inclusive
 * (`tax/calculation/price_includes_tax = 1`). The adapter, however, hands us a mix of both
 * bases, and reading the wrong one produced two visible bugs:
 *
 *  - `rowTotalWithDiscount` derives from Magento's `row_total`, which EXCLUDES tax. Showing it
 *    quoted a 95 kr cap at 76 kr, and a 4 300 kr rifle at 3 440 kr — 860 kr under what the
 *    customer actually pays.
 *  - `rowTotalRegular` comes from `price_range.regular_price`, which Magento renders per the
 *    display setting above and therefore INCLUDES tax. Compared against the excluding-tax line
 *    price, every correctly-taxed product looked discounted by exactly its VAT rate: 76 against
 *    95 reads as 20% off.
 *
 * Only a handful of products show it today, because most of the catalogue carries a tax class
 * that matches no tax rule and is taxed at 0% — where the two bases are identical and the bug is
 * invisible. That is a data problem on the Magento side and the client's to resolve; this module
 * is written so the storefront is right either way, and stays right the day the data is fixed.
 */

/**
 * What the customer pays for this line, including VAT and after any cart-rule discount.
 *
 * The discount is recovered from the excluding-tax pair rather than read directly, because the
 * adapter never exposes `total_item_discount` on its own — it only ever hands over
 * `rowTotal` and the already-subtracted `rowTotalWithDiscount`. Subtracting the two gives that
 * figure back, and it is the same number on both bases: with "Apply Discount On Prices" set to
 * Including Tax (`tax/calculation/discount_tax = 1`), Magento computes the discount on
 * tax-inclusive prices, so it needs no conversion before coming off an inclusive total.
 *
 * Note that a catalogue special price is NOT a discount in this sense — Magento folds it into the
 * line price itself and reports no item discount, which is why `rowTotalRegular` exists and why
 * the struck-through original is read from there instead.
 */
export function lineDisplayPrice(prices: CartItemPrices): number {
  const discount = prices.rowTotal - prices.rowTotalWithDiscount;

  return prices.rowTotalIncludingTax - discount;
}

/**
 * The undiscounted line price to strike through, including VAT — or `undefined` when there is
 * nothing to strike.
 *
 * `rowTotalRegular` already includes tax. Its documented fallback is `rowTotal`, which does not,
 * so the fallback is swapped for the inclusive equivalent: taking the excluding-tax one would
 * reintroduce exactly the phantom discount this module exists to remove.
 *
 * Returns `undefined` rather than an equal value when the line is not discounted, so callers
 * cannot accidentally render "95 kr" struck through beside "95 kr" — `LineItemPrice` hides the
 * original only when it is strictly greater, and this keeps that decision in one place.
 */
export function lineOriginalPrice(prices: CartItemPrices): number | undefined {
  const original = prices.rowTotalRegular ?? prices.rowTotalIncludingTax;

  return original > lineDisplayPrice(prices) ? original : undefined;
}
